/**
 * Marketing kit card on the Ambassador dashboard — printable flyer /
 * business card (opens the print view) and social-share PNGs (canvas).
 */
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { FileText, CreditCard, Smartphone, Square, Printer } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { renderSocialImage, downloadCanvas } from '@/utils/socialShareImage';

const Tile = ({ icon: Icon, title, hint, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group text-left rounded-xl border border-gray-200 bg-white p-3 hover:border-[#7A0B7A] hover:shadow-sm transition-all"
  >
    <div className="w-9 h-9 rounded-lg bg-[#7A0B7A]/10 flex items-center justify-center mb-2 group-hover:bg-[#7A0B7A]/20">
      <Icon className="w-4 h-4 text-[#7A0B7A]" />
    </div>
    <p className="text-sm font-bold text-gray-900">{title}</p>
    <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{hint}</p>
  </button>
);

export const MarketingKitCard = ({ affiliate, shareUrl }) => {
  const hiddenQrRef = useRef(null);
  const [busy, setBusy] = useState(null);

  const openPrint = (format) => {
    window.open(`/affiliate/print?format=${format}`, '_blank', 'noopener');
  };

  const makeSocial = (format) => {
    const qr = hiddenQrRef.current?.querySelector('canvas');
    if (!qr) { toast.error('QR code not ready yet — try again in a moment.'); return; }
    setBusy(format);
    try {
      const canvas = renderSocialImage({
        format,
        qrCanvas: qr,
        ambassadorName: affiliate.name,
        shareUrl,
        commissionLine: 'Free to join · Trade pricing · Installer training',
      });
      downloadCanvas(canvas, `ox-resin-${format}-${affiliate.slug}.png`);
      toast.success(format === 'story' ? 'Story image saved — post it straight to Instagram or Facebook.' : 'Square post saved — ready for your feed.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="p-4" data-testid="marketing-kit-card">
      <div className="flex items-center gap-2 mb-1">
        <Printer className="w-4 h-4 text-[#7A0B7A]" />
        <h2 className="text-base font-bold text-gray-900">Marketing kit</h2>
      </div>
      <p className="text-xs text-gray-500 mb-3">Ready-made print and social assets with your QR code and link already on them.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Tile icon={FileText} title="A5 flyer" hint="Print or save as PDF · leave-behind for customers" onClick={() => openPrint('a5')} testId="kit-flyer-a5" />
        <Tile icon={CreditCard} title="Business card" hint="85×55mm · 10 per A4 sheet" onClick={() => openPrint('card')} testId="kit-business-card" />
        <Tile icon={Smartphone} title={busy === 'story' ? 'Rendering…' : 'Story image'} hint="1080×1920 · Instagram & Facebook stories" onClick={() => makeSocial('story')} testId="kit-social-story" />
        <Tile icon={Square} title={busy === 'square' ? 'Rendering…' : 'Square post'} hint="1080×1080 · Instagram feed, Facebook, LinkedIn" onClick={() => makeSocial('square')} testId="kit-social-square" />
      </div>
      <div ref={hiddenQrRef} className="hidden" aria-hidden="true">
        <QRCodeCanvas value={shareUrl} size={800} level="M" bgColor="#ffffff" fgColor="#0B0B0E" includeMargin={false} />
      </div>
    </Card>
  );
};
